import {
  ButtonToggle,
  Flex,
  Skeleton,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import type { ReservationMode } from '@iheartjane/types';
import { useEffect, useRef } from 'react';

import { useEcommApp } from '@jane/shared/providers';

import { useCustomerDispatch } from '../../../../customer/dispatch';
import {
  selectCart,
  updateReservationMode,
} from '../../../../customer/redux/cart';
import { useCustomerSelector } from '../../../../customer/selectors';
import styles from './cartDrawerToggle.module.css';

interface CartDrawerToggleProps {
  /**
   * Internal flag to configure headless styles to be initially tested in Bloom
   * @private
   */
  bloomUserExperience?: boolean;
  deliveryAddress?: string;
  deliveryLabel: string;
  hasDeliverySlots: boolean;
  hasPickupSlots: boolean;
  isAcceptingDeliveryOrders: boolean;
  isLoading: boolean;
  modesToRender: ReservationMode[];
  pickupLabel: string;
  refetchDelivery: () => void;
  refetchPickup: () => void;
}

export const CartDrawerToggle = ({
  bloomUserExperience,
  deliveryAddress,
  deliveryLabel,
  hasDeliverySlots,
  hasPickupSlots,
  isAcceptingDeliveryOrders,
  isLoading,
  modesToRender,
  pickupLabel,
  refetchDelivery,
  refetchPickup,
}: CartDrawerToggleProps) => {
  const hasToggledRef = useRef(false);
  const { appMode } = useEcommApp();
  const isHeadless = appMode === 'headless' || bloomUserExperience;
  const dispatch = useCustomerDispatch();
  const {
    cart: { reservation_mode: currentReservationMode },
    userGroupSpecialId,
  } = useCustomerSelector(selectCart);

  const handleReservationModeToggle = (reservationMode: ReservationMode) => {
    dispatch(
      updateReservationMode({
        reservationMode,
        userGroupSpecialId,
      })
    );
  };

  useEffect(() => {
    const reservationMode = modesToRender.includes(currentReservationMode)
      ? currentReservationMode
      : modesToRender[0];

    if (reservationMode !== currentReservationMode) {
      handleReservationModeToggle(reservationMode);
    }
  }, []);

  const numberOfModes = modesToRender?.length || 2;
  const defaultMode = currentReservationMode || modesToRender[0];

  return (
    <Flex width="100%" justifyContent="center" mb={isHeadless ? 16 : 0} mt={16}>
      {isLoading ? (
        <Flex width="100%" gap={16}>
          {Array.from({ length: numberOfModes }).map((_, index) => (
            <Skeleton.Bone
              height="48px"
              width={numberOfModes > 1 ? '50%' : '100%'}
              borderRadius="lg"
              key={index}
            />
          ))}
        </Flex>
      ) : numberOfModes > 1 ? (
        <ButtonToggle
          variant="tertiary"
          value={currentReservationMode}
          onChange={(mode: ReservationMode) =>
            handleReservationModeToggle(mode)
          }
        >
          {modesToRender.map((mode) => {
            const isAcceptingDelivery =
              isAcceptingDeliveryOrders || hasDeliverySlots;
            const isAcceptingPickup = hasPickupSlots;

            const isClosed =
              (mode === 'delivery' && !isAcceptingDelivery) ||
              (mode === 'pickup' && !isAcceptingPickup);

            const labelToRender =
              (mode === 'delivery' && deliveryLabel) ||
              (mode === 'pickup' && pickupLabel);

            return (
              <ButtonToggle.Button
                autoFocus={
                  hasToggledRef.current && mode === currentReservationMode
                }
                className={
                  bloomUserExperience
                    ? styles['premiumToggleButton']
                    : undefined
                }
                key={mode}
                value={mode}
                disabled={isClosed}
                onClick={() => {
                  hasToggledRef.current = true;
                  if (mode === 'delivery' && !!deliveryAddress) {
                    refetchDelivery();
                  } else {
                    refetchPickup();
                  }
                }}
              >
                <Flex justifyContent="center">
                  <Typography color="inherit" as="span" variant="body-bold">
                    {labelToRender}
                  </Typography>
                  {isClosed && (
                    <Tag label="Closed" background="grays-black" ml={4} />
                  )}
                </Flex>
              </ButtonToggle.Button>
            );
          })}
        </ButtonToggle>
      ) : (
        <Typography variant="body-bold" textAlign="center">
          {(defaultMode === 'delivery' && deliveryLabel) ||
            (defaultMode === 'pickup' && pickupLabel)}
        </Typography>
      )}
    </Flex>
  );
};
